import React from 'react';
import { motion } from 'framer-motion';
import { CheckIcon } from '@heroicons/react/24/outline';

interface Step {
  id: number;
  title: string;
  description: string;
  icon?: React.ComponentType<any>;
}

interface ProgressIndicatorProps {
  steps: Step[];
  currentStep: number;
  completedSteps: number[];
}

const ProgressIndicator: React.FC<ProgressIndicatorProps> = ({
  steps,
  currentStep,
  completedSteps,
}) => {
  const getStepStatus = (stepId: number) => {
    if (completedSteps.includes(stepId)) return 'completed';
    if (stepId === currentStep) return 'current';
    return 'upcoming';
  };

  const getStepIcon = (stepId: number, status: string) => {
    if (status === 'completed') {
      return <CheckIcon className="h-3 w-3 text-white" />;
    }
    return <span className="text-xs font-semibold text-white">{stepId}</span>;
  };

  const getStepColor = (status: string) => {
    switch (status) {
      case 'completed':
        return 'bg-green-500 border-green-500';
      case 'current':
        return 'bg-purple-500 border-purple-500';
      default:
        return 'bg-gray-300 border-gray-300';
    }
  };

  const getProgressPercentage = () => {
    return ((currentStep - 1) / steps.length) * 100;
  };

  return (
    <div className="px-2">
      {/* Compact Step Indicators */}
      <div className="flex items-center justify-center max-w-sm mx-auto">
        {steps.map((step, index) => {
          const status = getStepStatus(step.id);
          const isLast = index === steps.length - 1;

          return (
            <div key={step.id} className="flex items-center">
              {/* Compact Step Circle */}
              <motion.div
                className={`w-5 h-5 rounded-full border flex items-center justify-center transition-all duration-300 ${getStepColor(status)}`}
                initial={{ scale: 0.8 }}
                animate={{ scale: 1 }}
              >
                {getStepIcon(step.id, status)}
              </motion.div>

              {/* Connector Line */}
              {!isLast && (
                <div className="w-8 mx-1">
                  <div
                    className={`h-px transition-all duration-300 ${
                      completedSteps.includes(step.id) || status === 'completed'
                        ? 'bg-green-500'
                        : 'bg-gray-300'
                    }`}
                  />
                </div>
              )}
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default ProgressIndicator;

